import api from '~/services/api';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

const MySwal = withReactContent(Swal);

type LojaId = string | number;

type DataForm = {
  cod_ncm: { value: string | number };
  cod_tipo_item: { value: string | number; label: string };
};

type LojaPrincipal = {
  cod_loja: LojaId;
  des_uf: string;
};

type NcmTipificacao = {
  des_sigla: string;
  cod_tipo_item: string | number;
};

type LojaApiResponse = {
  success: boolean;
  data?: {
    cod_loja: LojaId;
    des_uf: string;
  };
};

const ALERT_WIDTH = 650;

const escapeHtml = (value: unknown): string => {
  const str = String(value ?? '');
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

const fireAlert = (html: string): void => {
  MySwal.fire({
    width: ALERT_WIDTH,
    html,
  });
};

const buildTipoItemAlert = (tipoItemLabel: string): string => `
  <div>
    <div>
      ⚠️
      <h5>NCM não tem tipificação correspondente ao Tipo de Item informado.</h5>
    </div>
    <div>Tipo de Item: ${escapeHtml(tipoItemLabel)}</div>
    <div>
      Vá ao cadastro deste NCM e adicione a tipificação correspondente.
    </div>
  </div>
`;

const buildUfAlert = (ufsText: string): string => `
  <div>
    <div>
      ⚠️
      <h5>
        NCM selecionado não tem tributação informada para a unidade
        federativa da loja.
      </h5>
    </div>
    <div>
      Vá até o cadastro de NCM e adicione a tributação referente ao estado
    </div>
    <div>${escapeHtml(ufsText)}</div>
  </div>
`;

const fetchLojaInfo = async (codLoja: LojaId) => {
  try {
    const { data } = await api.get<LojaApiResponse>(`/lojas/${codLoja}`);
    if (!data?.success || !data?.data?.des_uf) return null;

    return {
      cod_loja: data.data.cod_loja,
      des_uf: data.data.des_uf,
    };
  } catch {
    return null;
  }
};

const uniq = (items: string[]) => Array.from(new Set(items));

export const validaTipoItemNcmDepartamento = async (
  dataForm: DataForm,
  mainLoja: LojaPrincipal,
  lojas: LojaId[] | null | undefined,
): Promise<boolean> => {
  try {
    const codNcmValue =
      typeof dataForm.cod_ncm === 'number'
        ? dataForm.cod_ncm
        : dataForm.cod_ncm?.value;
    const tipoItem = dataForm.cod_tipo_item.value;

    const res = await api.post('/produto/ncm-departamento/', {
      cod_ncm: codNcmValue,
    });

    const ncm = (res?.data?.data ?? []) as NcmTipificacao[];

    const tipificacaoDaUfPrincipal = ncm.filter(
      (item) =>
        item.des_sigla === mainLoja.des_uf && item.cod_tipo_item === tipoItem,
    );

    const existeTipoItemNoNcm = ncm.some(
      (item) => item.cod_tipo_item === tipoItem,
    );

    if (!existeTipoItemNoNcm) {
      fireAlert(buildTipoItemAlert(dataForm.cod_tipo_item.label));
      return true;
    }

    if (Array.isArray(lojas) && lojas.length > 0) {
      const outrasLojas = lojas.filter((lj) => lj !== mainLoja.cod_loja);

      const infosOutrasLojas = (
        await Promise.all(outrasLojas.map((lj) => fetchLojaInfo(lj)))
      ).filter(Boolean) as Array<{ cod_loja: LojaId; des_uf: string }>;

      const lojasSemTipificacao = infosOutrasLojas.filter((loja) => {
        const hasTipificacao = ncm.some(
          (itemNcm) =>
            itemNcm.des_sigla === loja.des_uf &&
            itemNcm.cod_tipo_item === tipoItem,
        );
        return !hasTipificacao;
      });

      if (lojasSemTipificacao.length > 0) {
        const ufsSemTipificacao = lojasSemTipificacao
          .map((lj) => lj.des_uf)
          .filter(Boolean);

        const ufsParaExibir =
          tipificacaoDaUfPrincipal.length === 0
            ? uniq([
                mainLoja.des_uf,
                ...ufsSemTipificacao.filter((uf) => uf !== mainLoja.des_uf),
              ])
            : uniq(ufsSemTipificacao);

        fireAlert(buildUfAlert(ufsParaExibir.join(' - ')));
        return true;
      }
    }

    if (tipificacaoDaUfPrincipal.length === 0) {
      fireAlert(buildUfAlert(mainLoja.des_uf));
      return true;
    }

    return false;
  } catch {
    fireAlert(`
      <div>
        <div>⚠️ <h5>Não foi possível validar o NCM agora.</h5></div>
        <div>Verifique sua conexão e tente novamente.</div>
      </div>
    `);
    return true;
  }
};
